<script setup lang="ts">
  import {computed} from 'vue';
  import {useI18n} from 'vue-i18n';
  import useCpBem from '~/composables/useCpBem';
  import CpIconStandard from '~/components/cp/CpIcon/CpIconStandard/CpIconStandard.vue';
  import CpText from '~/components/cp/CpText/CpText.vue';
  import {formatAsCurrency, parsePriceInt} from '~/utils';
  import type {ICpArticle} from '~/types';

  // Props for the story, but values most come from Pdp Context
  const props = defineProps<{
    product: ICpArticle
  }>();
  const {b, e} = useCpBem('cp-section-pdp-price-shipping');
  const {t} = useI18n();
  const shippingPrice = computed(() => formatAsCurrency(props.product.shipping));

  const typeIcon = computed(() => {
    if (outStock.value) {
      return 'neutral-light';
    }
    if (props.product.freeShipping) {
      return 'mkt';
    }
    return 'neutral';
  });

  const outStock = computed(() => {
    return props.product.eol || props.product.stockStatus === -1;
  });
</script>

<template>
    <div v-if="product" :class="b">
        <div :class="e('iconBox')">
            <CpIconStandard icon="local_shipping" :type="typeIcon" :class="e('icon')" />
            <CpText v-if="product.freeShipping" variant="body-bold" :type="outStock ? 'neutral-light' : 'mkt-dark'" data-testid="pdp-free-shipping">
                {{ t('freeShipping') }}
            </CpText>
            <CpText v-else variant="body-regular" :type="outStock ? 'neutral-light' : 'neutral'" data-testid="pdp-shipping-price">
                {{ `${t('shipping')}: ${shippingPrice}` }}
            </CpText>
        </div>
        <div>
            <div v-if="product.isOffer || product.tPrice > parsePriceInt(product.price)" :class="e('priceOffer')">
                <CpText
                    variant="price-from"
                    :type="outStock ? 'neutral-light': 'neutral'"
                >
                    {{ formatAsCurrency(product.tPrice) }}
                </CpText>
            </div>
            <div>
                <CpText
                    :variant="product.isOffer || product.tPrice>parsePriceInt(product.price) ? 'price-offer': 'price-regular'"
                    :type="outStock ? 'neutral': product.isOffer || product.tPrice > parsePriceInt(product.price) ? 'mkt-dark' : 'neutral-dark'"
                >
                    {{ formatAsCurrency(parsePriceInt(product.price)) }}
                </CpText>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
  .cp-section-pdp-price-shipping {
    display: flex;
    align-items: center;
    justify-content: space-between;

    padding: $cp-gutter-small $cp-gutter;
    border-bottom: 1px solid var(--color-neutral-extra-light);

    &__icon {
      margin-right: $cp-gutter-xsmall;
    }
    &__icon-box {
      display: flex;
      align-items: center;
    }
    &__price-offer {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }
  }
</style>
<i18n locale="es-US">
  shipping: 'Envío'
  freeShipping: 'Envío gratis'
</i18n>
